import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import HelpChatPanel from './HelpChatPanel';
import { getLastHelpTarget, clearLastHelpTarget } from '../../hooks/useHelpTarget';
import './Help.css';

// Routes where the help orb should not appear. Login is unauthenticated;
// /help itself is the help docs surface and doesn't need the floating shortcut.
const SUPPRESSED_PATTERNS = [/^\/login/, /^\/help(\/|$)/, /^\/case\/[^/]+\/view\//];

/**
 * HelpOrb — floating ? button + three context-aware entry points.
 *
 *   1. Click the orb. If the user interacted with a useHelpTarget()-tagged
 *      region in the last 30s, the panel opens scoped to that target.
 *      Otherwise it opens to the empty chat.
 *
 *   2. Right-click any useHelpTarget()-tagged region. The hook dispatches
 *      ``helpTarget:open``; the orb opens scoped to the target.
 *
 *   3. Press ⌘ /  (Ctrl-/ on non-Mac). Same as (1) — picks up the last
 *      target if recent, opens otherwise.
 */
export default function HelpOrb() {
  const [open, setOpen] = useState(false);
  const [initialScope, setInitialScope] = useState(null);
  const location = useLocation();

  // Right-click on a tagged region → open scoped.
  useEffect(() => {
    const handler = (e) => {
      setInitialScope(e.detail || null);
      setOpen(true);
    };
    window.addEventListener('helpTarget:open', handler);
    return () => window.removeEventListener('helpTarget:open', handler);
  }, []);

  // ⌘ / keyboard shortcut. Picks up last target if recent.
  useEffect(() => {
    const onKey = (e) => {
      // Don't fire while the user is typing in a text input or textarea.
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      const isSlash = e.key === '/' || e.key === '?';
      if ((e.metaKey || e.ctrlKey) && isSlash) {
        e.preventDefault();
        setInitialScope(getLastHelpTarget());
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (SUPPRESSED_PATTERNS.some((re) => re.test(location.pathname))) {
    return null;
  }

  const handleManualOpen = () => {
    setInitialScope(getLastHelpTarget());
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setInitialScope(null);
    clearLastHelpTarget();
  };

  return (
    <>
      {!open && (
        <button
          className="help-orb"
          onClick={handleManualOpen}
          aria-label="Open help"
          title="Help (⌘/). Right-click any control to ask about it."
        >
          ?
        </button>
      )}
      {open && <HelpChatPanel onClose={handleClose} initialScope={initialScope} />}
    </>
  );
}
